import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ComboboxItem<T extends string> {
  value: T;
  label: string;
  /** Optional sub-label rendered under the label (e.g. country name). */
  hint?: string;
  /** Left-side icon — flag emoji, currency symbol, lucide icon, etc. */
  icon?: ReactNode;
  /** Right-side small chip (e.g. ISO code in a muted pill). */
  badge?: ReactNode;
  /** Extra search tokens (e.g. native name, alt spellings). Joined into the searchable string. */
  searchAliases?: string[];
}

interface Props<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: ComboboxItem<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Visible label, optional — controlled by parent if it wraps in its own `<label>`. */
  label?: string;
  /** Hide the search input even when items.length > 8 (rare). */
  hideSearch?: boolean;
  /** Right-align the popover (default — picker is usually end-positioned in dialogs). */
  align?: 'left' | 'right';
}

/**
 * Reusable searchable popover-select. Used by CurrencyPicker,
 * CountryPicker, EmploymentTypePicker etc. — anywhere a native
 * `<select>` would look out of place against the design system.
 *
 * Accessibility:
 *   - aria-haspopup + aria-expanded on the trigger
 *   - role="listbox" on the popover; role="option" + aria-selected on items
 *   - keyboard: Escape closes, ArrowUp/Down navigates, Enter selects
 */
export function Combobox<T extends string>({
  value, onChange, items, placeholder, searchPlaceholder, disabled, className,
  hideSearch, align = 'right',
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = useMemo(() => items.find((i) => i.value === value), [items, value]);
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase().trim();
    return items.filter((i) => {
      const hay = [i.label, i.hint ?? '', i.value, ...(i.searchAliases ?? [])].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  // Close on outside-click / Escape; focus the search on open.
  useEffect(() => {
    if (!open) return;
    setActiveIdx(Math.max(0, filtered.findIndex((i) => i.value === value)));
    setTimeout(() => searchRef.current?.focus(), 30);
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     {
        e.preventDefault();
        const it = filtered[activeIdx];
        if (it) { onChange(it.value); setOpen(false); }
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, filtered, value, onChange, activeIdx]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const showSearch = !hideSearch && items.length > 6;

  return (
    <div className={`relative ${className ?? ''}`} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-medium hover:bg-muted focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition disabled:opacity-60 disabled:pointer-events-none"
      >
        {selected ? (
          <>
            {selected.icon && <span className="text-base shrink-0">{selected.icon}</span>}
            <span className="truncate text-start flex-1">{selected.label}</span>
            {selected.badge && <span className="text-[10px] text-muted-foreground shrink-0">{selected.badge}</span>}
          </>
        ) : (
          <span className="truncate text-muted-foreground">{placeholder ?? t('common.select', { defaultValue: 'Select…' })}</span>
        )}
        <ChevronDown className="h-4 w-4 opacity-60 shrink-0 ms-auto" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 top-11 w-full min-w-[280px] max-h-80 rounded-2xl glass-strong shadow-card-lift overflow-hidden flex flex-col ${align === 'right' ? 'end-0' : 'start-0'}`}
          >
            {showSearch && (
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                    placeholder={searchPlaceholder ?? t('common.searchEllipsis', { defaultValue: 'Search…' })}
                    className="w-full h-8 rounded-lg border border-border bg-background ps-8 pe-2 text-xs outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
            )}
            {filtered.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                {t('common.noResults', { defaultValue: 'No matches' })}
              </p>
            ) : (
              <ul ref={listRef} className="overflow-y-auto keenez-scrollbar py-1">
                {filtered.map((it, i) => {
                  const active = it.value === value;
                  const focused = i === activeIdx;
                  return (
                    <li key={it.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => { onChange(it.value); setOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-start transition ${
                          active ? 'bg-primary/10 text-primary' :
                          focused ? 'bg-muted text-foreground' :
                          'text-foreground/85'
                        }`}
                      >
                        {it.icon && <span className="text-base shrink-0">{it.icon}</span>}
                        <span className="flex-1 min-w-0">
                          <span className={`block truncate ${active ? 'font-semibold' : ''}`}>{it.label}</span>
                          {it.hint && <span className="block text-[10px] text-muted-foreground truncate">{it.hint}</span>}
                        </span>
                        {it.badge && <span className="text-[10px] text-muted-foreground shrink-0">{it.badge}</span>}
                        {active && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
